import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';

export interface AuthenticatedUser {
  id: string;
  name: string;
  email: string;
  role: string;
  department?: string;
  photo?: string;
}

interface AuthContextValue {
  getAccessToken?: () => Promise<string | undefined>;
  instanceId?: string;
  isProduction: boolean;
  authenticatedUser: AuthenticatedUser | null;
  setAuthenticatedUser: (user: AuthenticatedUser | null) => void;
}

const defaultContextValue: AuthContextValue = {
  getAccessToken: undefined,
  instanceId: undefined,
  isProduction: false,
  authenticatedUser: null,
  setAuthenticatedUser: () => {},
};

const AuthContext = createContext<AuthContextValue>(defaultContextValue);

interface AuthProviderProps {
  getAccessToken?: () => Promise<string | undefined>;
  instanceId?: string;
  isProduction?: boolean;
  children: ReactNode;
}

export function AuthProvider({ getAccessToken, instanceId, isProduction = false, children }: AuthProviderProps) {
  const [authenticatedUser, setAuthenticatedUserState] = useState<AuthenticatedUser | null>(null);

  const setAuthenticatedUser = useCallback((user: AuthenticatedUser | null) => {
    setAuthenticatedUserState(user);
  }, []);

  const value: AuthContextValue = {
    getAccessToken,
    instanceId,
    isProduction,
    authenticatedUser,
    setAuthenticatedUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
